import React, { useEffect, useRef, useState } from "react";
import { Clear, Draw, TextFields, Undo } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SignatureCanvas from "react-signature-canvas";

export interface SignatureCaptureProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

/** The existing patient-form wire format: a PNG data URI or typed-signature JSON. */
export function getTypedSignatureName(value: string): string | undefined {
  try {
    const parsed: unknown = JSON.parse(value);
    if (
      parsed &&
      typeof parsed === "object" &&
      "type" in parsed &&
      parsed.type === "typed" &&
      "name" in parsed &&
      typeof parsed.name === "string"
    )
      return parsed.name;
  } catch {
    /* Drawn signatures are data URIs. */
  }
  return undefined;
}

/** Controlled capture only. A changed value never constitutes agreement acceptance. */
export function SignatureCapture({
  value,
  onChange,
  disabled = false,
  placeholder = "Enter your full name",
}: SignatureCaptureProps) {
  const [mode, setMode] = useState<"draw" | "type">(() =>
    getTypedSignatureName(value) === undefined ? "draw" : "type",
  );
  const pad = useRef<SignatureCanvas>(null);
  const initialValue = useRef(value);
  useEffect(() => {
    if (initialValue.current.startsWith("data:image/png;"))
      pad.current?.fromDataURL(initialValue.current);
  }, []);
  const updateDrawing = () =>
    onChange(
      pad.current && !pad.current.isEmpty()
        ? pad.current.toDataURL("image/png")
        : "",
    );
  const clear = () => {
    pad.current?.clear();
    onChange("");
  };
  const typedName = getTypedSignatureName(value) ?? "";
  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <ToggleButtonGroup
        value={mode}
        exclusive
        fullWidth
        size="small"
        disabled={disabled}
        onChange={(_, next: "draw" | "type" | null) => {
          if (next && next !== mode) {
            clear();
            setMode(next);
          }
        }}
      >
        <ToggleButton value="draw" aria-label="Draw signature">
          <Draw sx={{ mr: 1 }} />
          Draw signature
        </ToggleButton>
        <ToggleButton value="type" aria-label="Type signature">
          <TextFields sx={{ mr: 1 }} />
          Type name
        </ToggleButton>
      </ToggleButtonGroup>
      {mode === "draw" ? (
        <Paper
          variant="outlined"
          sx={{
            position: "relative",
            height: 200,
            overflow: "hidden",
            pointerEvents: disabled ? "none" : undefined,
            "& canvas": { width: "100%", height: "100%", touchAction: "none" },
          }}
        >
          <SignatureCanvas
            ref={pad}
            canvasProps={{
              "aria-label": "Draw your signature",
            }}
            backgroundColor="white"
            penColor="black"
            // Patient-form safeguard: mobile keyboard / browser chrome resize must not clear a stroke mid-gesture.
            clearOnResize={false}
            onEnd={updateDrawing}
          />
          <Box sx={{ position: "absolute", top: 8, right: 8 }}>
            <IconButton
              aria-label="Undo last stroke"
              disabled={disabled || !value}
              onClick={() => {
                const strokes = pad.current?.toData();
                if (strokes) {
                  strokes.pop();
                  pad.current?.fromData(strokes);
                  updateDrawing();
                }
              }}
            >
              <Undo />
            </IconButton>
            <IconButton
              aria-label="Clear signature"
              disabled={disabled || !value}
              onClick={clear}
            >
              <Clear />
            </IconButton>
          </Box>
          {!value && (
            <Typography
              color="text.secondary"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                pointerEvents: "none",
              }}
            >
              Sign here
            </Typography>
          )}
        </Paper>
      ) : (
        <>
          <TextField
            label="Type your full name"
            value={typedName}
            disabled={disabled}
            placeholder={placeholder}
            onChange={(event) =>
              onChange(
                event.target.value.trim()
                  ? JSON.stringify({
                      type: "typed",
                      name: event.target.value,
                      timestamp: new Date().toISOString(),
                    })
                  : "",
              )
            }
          />
          {typedName && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
              <Typography sx={{ fontFamily: "cursive", fontSize: 32 }}>
                {typedName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Preview of your signature
              </Typography>
            </Paper>
          )}
          {typedName && (
            <Box>
              <IconButton
                aria-label="Clear signature"
                disabled={disabled}
                onClick={clear}
              >
                <Clear />
              </IconButton>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export function SignatureDisplay({
  value,
  label,
}: {
  value: string;
  label: string;
}) {
  const name = getTypedSignatureName(value);
  if (name)
    return (
      <Typography
        aria-label={label}
        sx={{ fontFamily: "cursive", fontSize: 32 }}
      >
        {name}
      </Typography>
    );
  if (value.startsWith("data:image/png;base64,"))
    return (
      <Box
        component="img"
        src={value}
        alt={label}
        sx={{ maxWidth: "100%", maxHeight: 100 }}
      />
    );
  return <Typography color="text.secondary">Signature unavailable</Typography>;
}
